import React, { useState, useEffect } from "react";
import { buildStops, fmt } from "../utils/sliderStops";

// ============================================================
// 필터 슬라이더 범위/단위 관리 카드 (관리 탭)
//
// 하는 일:
//   - GET  /api/slider-config 로 현재 설정을 불러온다
//   - 항목별로 시작값(min)과 구간(여기까지 / 이 단위로)을 편집한다
//   - POST /api/slider-config 로 저장한다
//
// 연관 파일:
//   - utils/sliderStops.js : 설정 → 정류장 배열 계산 (미리보기에 사용)
//   - App.js 관리탭 <main> : 이 카드를 배치하는 곳
//   - (예정) 필터 패널 슬라이더가 같은 설정을 읽어 동작
//
// ※ App.js 안이 아니라 별도 파일로 만든 이유:
//    컴포넌트를 다른 컴포넌트 안에 정의하면 부모가 다시 그려질 때마다
//    새 컴포넌트로 인식되어 DOM 이 통째로 교체된다(입력 중 포커스가 날아감).
// ============================================================

// 화면에 보여줄 한글 이름. DB 에는 영문 키만 저장하므로 여기서 짝을 맞춘다.
// (DB 문자셋 문제로 한글이 깨진 사례가 있어 의도적으로 분리함)
const LABELS = {
  room_cnt:               "룸수",
  bath_cnt:               "욕실수",
  fee_sale:               "매매금액 (만원)",
  fee_year:               "보증금 (만원)",
  fee_month:              "월세 (만원)",
  floor:                  "해당층",
  exclusive_scale_square: "전용면적 (평)",
  used_scale_square:      "실면적 (평)",
  completion_year:        "건물노후기간 (년)",
  owner_room:             "주인세대 룸수",
  owner_bath:             "주인세대 욕실수",
};

const ORDER = Object.keys(LABELS);

export default function SliderConfigManager({ cardStyle, titleStyle, subTitleStyle }) {
  const [cfg, setCfg]         = useState(null);   // 편집 중인 설정
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState("");     // 사용자 안내 문구

  // ── 최초 1회 불러오기 ──────────────────────────────────
  useEffect(() => {
    let alive = true; // 응답 도착 전에 화면을 떠나면 setState 하지 않도록
    fetch("/api/slider-config")
      .then((r) => r.json())
      .then((d) => { if (alive) { setCfg(d.slider_config || {}); setLoading(false); } })
      .catch(() => { if (alive) { setMsg("설정을 불러오지 못했습니다."); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  // ── 편집 헬퍼 ─────────────────────────────────────────
  const setMin = (key, v) =>
    setCfg((c) => ({ ...c, [key]: { ...c[key], min: v } }));

  const setSeg = (key, idx, field, v) =>
    setCfg((c) => {
      const segs = [...(c[key]?.segments || [])];
      segs[idx] = { ...segs[idx], [field]: v };
      return { ...c, [key]: { ...c[key], segments: segs } };
    });

  // 구간 추가: 마지막 구간을 기준으로 적당히 이어붙인다
  const addSeg = (key) =>
    setCfg((c) => {
      const segs = [...(c[key]?.segments || [])];
      const last = segs[segs.length - 1];
      const to   = Number(last?.to ?? 10) * 2;
      const step = Number(last?.step ?? 1) * 5;
      segs.push({ to, step });
      return { ...c, [key]: { ...c[key], segments: segs } };
    });

  // 구간 삭제 (최소 1개는 남긴다 — 0개면 서버가 기본값으로 되돌린다)
  const delSeg = (key, idx) =>
    setCfg((c) => {
      const segs = (c[key]?.segments || []).filter((_, i) => i !== idx);
      return { ...c, [key]: { ...c[key], segments: segs.length ? segs : c[key].segments } };
    });

  // ── 저장 ──────────────────────────────────────────────
  const save = async () => {
    setMsg("저장 중...");
    try {
      const res = await fetch("/api/slider-config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slider_config: cfg }),
      });
      const d = await res.json();
      if (d.ok) {
        // 서버가 검증한 결과를 되받아 반영한다.
        // 잘못 입력한 값은 여기서 자동 교정된 모습으로 보인다.
        setCfg(d.slider_config);
        setMsg("저장했습니다. 필터 화면에 바로 적용됩니다.");
      } else {
        setMsg("저장 실패: " + (d.error || "알 수 없는 오류"));
      }
    } catch (e) {
      setMsg("저장 실패: " + e.message);
    }
  };

  // ── 화면 ──────────────────────────────────────────────
  if (loading) {
    return (
      <div style={cardStyle}>
        <h3 style={titleStyle}>슬라이더 범위 설정</h3>
        <p style={subTitleStyle}>불러오는 중...</p>
      </div>
    );
  }

  const inputStyle = {
    width: "90px", height: "28px", padding: "0 6px",
    border: "1px solid #cbd5e1", borderRadius: "5px", fontSize: "13px",
  };

  return (
    <div style={cardStyle}>
      <h3 style={titleStyle}>슬라이더 범위 설정</h3>
      <p style={subTitleStyle}>
        검색 필터 슬라이더가 움직이는 범위와 간격을 정합니다.
        구간을 나누면 금액대별로 다른 간격을 쓸 수 있습니다.
      </p>

      <div style={{ marginTop: "18px", display: "flex", flexDirection: "column", gap: "14px" }}>
        {ORDER.map((key) => {
          const item  = cfg[key] || { min: 0, segments: [] };
          const stops = buildStops(item);       // 미리보기용 정류장 계산
          const tooMany = stops.length > 200;   // 너무 촘촘하면 조작이 힘들다

          return (
            <div key={key} style={{
              padding: "12px", background: "#f8fafc",
              border: "1px solid #e2e8f0", borderRadius: "8px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                <span style={{ fontSize: "14px", fontWeight: 700, color: "#1e293b" }}>
                  {LABELS[key]}
                </span>
                <span style={{ fontSize: "12px", color: tooMany ? "#dc2626" : "#64748b" }}>
                  칸 {stops.length}개
                  {stops.length > 1 && ` (${fmt(stops[0])} ~ ${fmt(stops[stops.length - 1])})`}
                  {tooMany && "  너무 촘촘합니다"}
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                <span style={{ fontSize: "12px", color: "#64748b", width: "52px" }}>시작값</span>
                <input
                  type="number" style={inputStyle} value={item.min ?? 0}
                  onChange={(e) => setMin(key, e.target.value)}
                />
                <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                  (지하층이 있으면 음수 가능)
                </span>
              </div>

              {(item.segments || []).map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "5px" }}>
                  <span style={{ fontSize: "12px", color: "#64748b", width: "52px" }}>
                    구간 {i + 1}
                  </span>
                  {/* [SEG_LABEL_FIX_V1] 숫자+라벨을 한 묶음으로 감싸 읽기 쉽게 정렬 */}
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    <input
                      type="number" style={inputStyle} value={s.to ?? ""}
                      onChange={(e) => setSeg(key, i, "to", e.target.value)}
                    />
                    <span style={{ fontSize: "12px", color: "#475569" }}>까지</span>
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", marginLeft: "8px" }}>
                    <input
                      type="number" style={inputStyle} value={s.step ?? ""}
                      onChange={(e) => setSeg(key, i, "step", e.target.value)}
                    />
                    <span style={{ fontSize: "12px", color: "#475569" }}>씩</span>
                  </span>
                  <button
                    onClick={() => delSeg(key, i)}
                    style={{
                      marginLeft: "4px", fontSize: "11px", color: "#dc2626",
                      background: "none", border: "1px solid #fecaca",
                      borderRadius: "4px", padding: "2px 7px", cursor: "pointer",
                    }}
                  >삭제</button>
                </div>
              ))}

              <button
                onClick={() => addSeg(key)}
                style={{
                  marginTop: "4px", fontSize: "12px", color: "#2563eb",
                  background: "none", border: "1px dashed #93c5fd",
                  borderRadius: "5px", padding: "4px 10px", cursor: "pointer",
                }}
              >+ 구간 추가</button>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: "18px", display: "flex", alignItems: "center", gap: "12px" }}>
        <button
          onClick={save}
          style={{
            padding: "9px 22px", background: "#2563eb", color: "#fff",
            border: "none", borderRadius: "6px", fontSize: "14px",
            fontWeight: 600, cursor: "pointer",
          }}
        >설정 저장</button>
        {msg && <span style={{ fontSize: "13px", color: "#475569" }}>{msg}</span>}
      </div>
    </div>
  );
}
